import {
  ESButton,
  ESDialogActions,
  ESDialogContent,
  ESDialogTitle,
  useESSnackbar,
} from '@energy-stacks/core/ui';
import { Checkbox, Dialog, FormControlLabel, TextField } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import {
  ChargingStationDetailsModel,
  SignedUpdateFirmwareRequestBody,
  UpdateFirmware201RequestBody,
  UpdateFirmware21RequestBody,
  UpdateFirmwareV16RequestBody,
  useSignedUpdateFirmwareV16Mutation,
  useUpdateFirmwareV16Mutation,
  useUpdateFirmwareV201Mutation,
  useUpdateFirmwareV21Mutation,
} from '@energy-stacks/broker/feature-charging-stations-data';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getFirmwareUpdateVariant,
  validateFirmwareUpdatePayload,
} from './validateFirmwareUpdatePayload';

interface FirmwareUpdateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  identityKey: ChargingStationDetailsModel['identityKey'];
  ocppProtocol: ChargingStationDetailsModel['ocppProtocol'];
}

interface FirmwareUpdateFormData {
  payload: string;
  signed: boolean;
}

export const FirmwareUpdateDialog: React.FC<FirmwareUpdateDialogProps> = ({
  isOpen,
  onClose,
  identityKey,
  ocppProtocol,
}) => {
  const { t } = useTranslation('chargingStations');
  const { showSnackbar } = useESSnackbar();
  const isV16 = ocppProtocol === 'V_16';

  const {
    control,
    handleSubmit,
    reset,
    watch,
    trigger,
    formState: { isValid, errors },
    getValues,
  } = useForm<FirmwareUpdateFormData>({
    resolver: yupResolver(
      yup.object().shape({
        payload: yup
          .string()
          .required(t('firmwareUpdatePayloadRequired'))
          .test('firmware-payload', '', function (value) {
            const signed = Boolean(this.parent.signed);
            const variant = getFirmwareUpdateVariant(ocppProtocol, signed);
            if (!variant) {
              return this.createError({
                message: t('firmwareUpdateUnsupportedProtocol'),
              });
            }

            const result = validateFirmwareUpdatePayload(value ?? '', variant);
            if (!result.valid) {
              return this.createError({
                message: result.errors.map((errorKey) => t(errorKey)).join(' '),
              });
            }

            return true;
          }),
        signed: yup.boolean().required(),
      })
    ),
    mode: 'onChange',
    defaultValues: {
      payload: '',
      signed: false,
    },
  });

  const signed = watch('signed');

  useEffect(() => {
    if (getValues('payload')) {
      void trigger('payload');
    }
  }, [signed, getValues, trigger]);

  const [updateFirmwareV16, { isLoading: isV16Loading }] =
    useUpdateFirmwareV16Mutation();
  const [signedUpdateFirmwareV16, { isLoading: isV16SignedLoading }] =
    useSignedUpdateFirmwareV16Mutation();
  const [updateFirmwareV201, { isLoading: isV201Loading }] =
    useUpdateFirmwareV201Mutation();
  const [updateFirmwareV21, { isLoading: isV21Loading }] =
    useUpdateFirmwareV21Mutation();

  const isLoading =
    isV16Loading || isV16SignedLoading || isV201Loading || isV21Loading;

  const requestRef = useRef<
    | ReturnType<typeof updateFirmwareV16>
    | ReturnType<typeof signedUpdateFirmwareV16>
    | ReturnType<typeof updateFirmwareV201>
    | ReturnType<typeof updateFirmwareV21>
  >();

  const onSubmit = useCallback(() => {
    const variant = getFirmwareUpdateVariant(ocppProtocol, getValues().signed);
    if (!variant) {
      showSnackbar('error', t('firmwareUpdateUnsupportedProtocol'));
      return;
    }

    const validation = validateFirmwareUpdatePayload(
      getValues().payload,
      variant
    );
    if (!validation.valid) {
      return;
    }

    let request:
      | ReturnType<typeof updateFirmwareV16>
      | ReturnType<typeof signedUpdateFirmwareV16>
      | ReturnType<typeof updateFirmwareV201>
      | ReturnType<typeof updateFirmwareV21>;

    if (variant === 'v16') {
      request = updateFirmwareV16({
        identityKey: identityKey,
        payload: validation.payload as unknown as UpdateFirmwareV16RequestBody,
      });
    } else if (variant === 'v16-signed') {
      request = signedUpdateFirmwareV16({
        identityKey: identityKey,
        payload:
          validation.payload as unknown as SignedUpdateFirmwareRequestBody,
      });
    } else if (variant === 'v201') {
      request = updateFirmwareV201({
        identityKey: identityKey,
        payload: validation.payload as unknown as UpdateFirmware201RequestBody,
      });
    } else {
      request = updateFirmwareV21({
        identityKey: identityKey,
        payload: validation.payload as unknown as UpdateFirmware21RequestBody,
      });
    }

    requestRef.current = request;

    request
      .unwrap()
      .then((result) => {
        if (result && 'status' in result) {
          if (
            result.status === 'Rejected' ||
            result.status === 'InvalidCertificate' ||
            result.status === 'RevokedCertificate'
          ) {
            showSnackbar('error', t('firmwareUpdateRejectedMessage'));
            return;
          }

          if (result.status === 'Accepted') {
            showSnackbar('success', t('firmwareUpdateSuccessMessage'));
          }

          if (result.status === 'AcceptedCanceled') {
            showSnackbar('info', t('firmwareUpdateAcceptedCanceledMessage'));
          }

          onClose();
          return;
        }

        showSnackbar('success', t('firmwareUpdateSuccessMessage'));
        onClose();
      })
      .catch((error) => {
        if (error.data?.errorCode === 'RPC_TIMEOUT') {
          showSnackbar('error', t('rpcTimeoutError'));
          return;
        }
        if (error.data?.errorCode === 'RPC_ERROR') {
          showSnackbar('error', t('rpcError'));
          return;
        }

        if (error.name === 'AbortError') {
          return;
        }

        showSnackbar('error');
      });
  }, [
    identityKey,
    ocppProtocol,
    updateFirmwareV16,
    signedUpdateFirmwareV16,
    updateFirmwareV201,
    updateFirmwareV21,
    getValues,
    onClose,
    t,
    showSnackbar,
  ]);

  const handleCancel = useCallback(() => {
    requestRef.current?.abort();
    reset();
    onClose();
  }, [onClose, reset]);

  return (
    <Dialog open={isOpen} onClose={handleCancel} fullWidth maxWidth="sm">
      <ESDialogTitle data-testid="firmwareUpdateTitle">
        {t('firmwareUpdate')}
      </ESDialogTitle>

      <ESDialogContent>
        <Controller
          control={control}
          name="payload"
          render={({ field: { value, onChange, onBlur } }) => (
            <TextField
              data-testid="firmwareUpdatePayloadField"
              label={t('firmwareUpdatePayload')}
              placeholder={t('firmwareUpdatePayloadPlaceholder')}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              disabled={isLoading}
              error={!!errors.payload}
              helperText={errors.payload?.message}
              required
              multiline
              minRows={8}
              fullWidth
              sx={{ mt: 8 }}
            />
          )}
        />

        {isV16 ? (
          <Controller
            control={control}
            name="signed"
            render={({ field: { value, onChange } }) => (
              <FormControlLabel
                sx={{ mt: 2 }}
                control={
                  <Checkbox
                    data-testid="signedFirmwareUpdateCheckbox"
                    checked={value}
                    onChange={(event) => onChange(event.target.checked)}
                    disabled={isLoading}
                  />
                }
                label={t('signedFirmwareUpdate')}
              />
            )}
          />
        ) : null}
      </ESDialogContent>

      <ESDialogActions>
        <ESButton
          data-testid="firmwareUpdateCancelButton"
          variant="text"
          color="error"
          onClick={handleCancel}
          disabled={isLoading}
        >
          {t('cancel')}
        </ESButton>

        <ESButton
          data-testid="firmwareUpdateSubmitButton"
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          disabled={!isValid || isLoading}
          loading={isLoading}
        >
          {t('firmwareUpdate')}
        </ESButton>
      </ESDialogActions>
    </Dialog>
  );
};
